import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';
import MoveIcon from '@cartwhl/ui/icons/MoveIcon';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';

import type {
  ChainSummary,
  PropertiesToolMode,
  ViewerToolMode,
} from '../../../cartographerStore';
import {
  hasNonIdentityScale,
  type BonePoseTransform,
} from '../../../lib/skeleton/boneTransforms';
import { type Skeleton } from '../../../lib/skeleton/core';
import type {
  OutlinerHierarchy,
  OutlinerTransformNode,
} from '../../../lib/outliner';
import type { MappingResult } from '../../../lib/recognize/config';
import {
  CHARACTER_AXIS_OPTIONS,
  type CharacterAxis,
} from '../../../lib/schema/characterAxes';
import { type ChainSolverProperties } from '../../../lib/schema/solver';
import { hasBoneProperties } from '../../../presentations/properties';
import {
  PANEL_WRITING_CLASSNAME,
  PANEL_WRITING_VARIANT,
} from '../../../presentations/panelWritingStyle';
import ChainList from './ChainList';
import JointOffsetEditor from './JointOffsetEditor';
import MappingInfo from './MappingInfo';
import PoseTransformEditor from './PoseTransformEditor';
import PreferredAnglesEditor from './PreferredAnglesEditor';
import RestTransformViewer from './RestTransformViewer';
import RotationLimitsEditor from './RotationLimitsEditor';
import Row from './Row';
import Section from './Section';
import SolverEditor from './SolverEditor';
import TransformAxisValuesRow from './TransformAxisValuesRow';

const TRANSFORM_ROWS_COMPACT_THRESHOLD_PX = 430;

interface InspectorProps {
  skeleton: Skeleton | null;
  outlinerHierarchy?: OutlinerHierarchy | null;
  selectedOutlinerNodeId?: string | null;
  mapping: MappingResult | null;
  selectedBoneIndex: number | null;
  excludedBoneRefs?: string[];
  chainSummaries: ChainSummary[];
  presetName: string | null;
  poseVersion?: number;
  onBonePropertiesChange?: () => void;
  onRecordBonePropertiesHistoryAction?: (
    label: string,
    action: () => void,
  ) => void;
  onBeginBonePropertiesHistoryTransaction?: (label: string) => void;
  onRecordPoseHistoryAction?: (label: string, action: () => void) => void;
  onBeginPoseHistoryTransaction?: (label: string) => void;
  onCommitHistoryTransaction?: () => void;
  onBonePoseTransformChange?: (
    boneIndex: number,
    transform: BonePoseTransform,
  ) => void;
  onResetBonePoseTransform?: (boneIndex: number) => void;
  onToggleBoneExcluded?: (boneRef: string) => void;
  onChainSolverTypeChange?: (chainName: string, solverType: string) => void;
  onChainSolverPropertiesChange?: (
    chainName: string,
    solverProperties: ChainSolverProperties,
  ) => void;
  onJointOffsetChange?: (
    boneRef: string,
    rotation: [number, number, number] | null,
  ) => void;
  onCharacterForwardAxisChange?: (axis: CharacterAxis) => void;
  onCharacterUpAxisChange?: (axis: CharacterAxis) => void;
  onViewerToolModeChange?: (mode: ViewerToolMode) => void;
  onPropertiesToolModeChange?: (mode: PropertiesToolMode) => void;
}

export default function Inspector({
  skeleton,
  outlinerHierarchy = null,
  selectedOutlinerNodeId = null,
  mapping,
  selectedBoneIndex,
  excludedBoneRefs = [],
  chainSummaries,
  presetName,
  poseVersion = 0,
  onBonePropertiesChange,
  onRecordBonePropertiesHistoryAction,
  onBeginBonePropertiesHistoryTransaction,
  onRecordPoseHistoryAction,
  onBeginPoseHistoryTransaction,
  onCommitHistoryTransaction,
  onBonePoseTransformChange,
  onResetBonePoseTransform,
  onToggleBoneExcluded,
  onChainSolverTypeChange,
  onChainSolverPropertiesChange,
  onJointOffsetChange,
  onCharacterForwardAxisChange,
  onCharacterUpAxisChange,
  onViewerToolModeChange,
  onPropertiesToolModeChange,
}: InspectorProps) {
  const [, setVersion] = useState(0);
  const transformRowsWidthRef = useRef<HTMLDivElement>(null);
  const [compactTransformRows, setCompactTransformRows] = useState(false);
  const bump = useCallback(() => {
    setVersion((v) => v + 1);
    onBonePropertiesChange?.();
  }, [onBonePropertiesChange]);
  const activatePropertiesToolMode = useCallback(
    (mode: PropertiesToolMode) => {
      onPropertiesToolModeChange?.(mode);
      onViewerToolModeChange?.('properties');
    },
    [onPropertiesToolModeChange, onViewerToolModeChange],
  );
  const excludedBoneSet = useMemo(
    () =>
      new Set<number>(
        excludedBoneRefs
          .map((ref) => skeleton?.resolveBoneRef(ref, { namespace: 'internal' }) ?? null)
          .filter((idx): idx is number => idx !== null),
      ),
    [excludedBoneRefs, skeleton],
  );
  const selectedOutlinerNode = useMemo(() => {
    if (!selectedOutlinerNodeId || !outlinerHierarchy) return null;
    return outlinerHierarchy.nodesById[selectedOutlinerNodeId] ?? null;
  }, [outlinerHierarchy, selectedOutlinerNodeId]);

  useLayoutEffect(() => {
    const element = transformRowsWidthRef.current;
    if (!element) return;

    const updateCompactState = () => {
      setCompactTransformRows(
        element.getBoundingClientRect().width <
          TRANSFORM_ROWS_COMPACT_THRESHOLD_PX,
      );
    };
    updateCompactState();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => updateCompactState());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  if (!skeleton) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Text
          variant={PANEL_WRITING_VARIANT}
          className={`text-center ${PANEL_WRITING_CLASSNAME}`}
        >
          Select a node in the outliner
        </Text>
      </div>
    );
  }

  if (selectedOutlinerNode?.kind === 'transform') {
    return (
      <TransformNodeInspector
        node={selectedOutlinerNode}
        hierarchy={outlinerHierarchy}
        compactLayout={compactTransformRows}
        transformRowsWidthRef={transformRowsWidthRef}
      />
    );
  }

  if (selectedBoneIndex === null) {
    return (
      <div className="translucent-white-scrollbar dark:bg-gray-90/20 flex h-full flex-col overflow-y-auto rounded-xl bg-white/20">
        {mapping && chainSummaries.length > 0 && (
          <>
            <Section title="Recognition" defaultOpen={false}>
              <ChainList
                chainSummaries={chainSummaries}
                presetName={presetName}
                boneCount={skeleton.bones.length}
                showTitle={false}
              />
            </Section>
            <Section title="Target Axis Configuration" defaultOpen={false}>
              <div className="flex flex-col gap-2">
                <AxisDropdownRow
                  label="Forward"
                  value={mapping.forwardAxis}
                  onChange={onCharacterForwardAxisChange ?? (() => {})}
                />
                <AxisDropdownRow
                  label="Up"
                  value={mapping.upAxis}
                  onChange={onCharacterUpAxisChange ?? (() => {})}
                />
              </div>
            </Section>
          </>
        )}
        <div className="flex flex-1 items-center justify-center p-4">
          <Text
            variant={PANEL_WRITING_VARIANT}
            className={`text-center ${PANEL_WRITING_CLASSNAME}`}
          >
            Select a node in the outliner
          </Text>
        </div>
      </div>
    );
  }

  const bone = skeleton.bone(selectedBoneIndex);
  const parentName =
    bone.parent !== null ? skeleton.bone(bone.parent).name : 'None';
  const isBoneExcluded = excludedBoneSet.has(bone.index);
  const excludedToggleTooltipText = isBoneExcluded
    ? 'Include this bone in mapping'
    : 'Exclude this bone from mapping';

  const toggleExcluded = () => {
    onToggleBoneExcluded?.(bone.internalRef);
  };
  const chainName = mapping?.getChainForBone(bone.internalRef) ?? null;
  const chainMapping = chainName
    ? (mapping?.chainResults[chainName] ?? null)
    : null;
  const hasSignificantProperties = hasBoneProperties(
    bone,
    mapping?.ioExtras ?? {},
  );

  return (
    <div className="translucent-white-scrollbar dark:bg-gray-90/20 flex h-full flex-col overflow-y-auto rounded-xl bg-white/20">
      <Section title="Details" defaultOpen={false}>
        <Row label="Parent" value={parentName} />
        <Row label="Children" value={bone.children.length} />
        <Row label="Depth" value={bone.depth} />
        <Row label="Subtree size" value={bone.subtreeSize} />
        <Row label="Length" value={bone.length.toFixed(2)} />
        <Row label="Auxiliary" value={bone.auxiliary ? 'Yes' : 'No'} />
        <Row label="Deforming" value={bone.deforming ? 'Yes' : 'No'} />
        <div className="flex items-center justify-between gap-2 py-1">
          <Text
            variant="studio/body/medium/xs"
            className={`${PANEL_WRITING_CLASSNAME} shrink-0`}
          >
            Excluded
          </Text>
          <div className="flex items-center">
            <Tooltip text={excludedToggleTooltipText} side="top" inModal>
              <button
                type="button"
                className={`h-3.5 w-7 rounded-full transition-colors ${isBoneExcluded ? 'bg-blue-50' : 'bg-gray-30 dark:bg-gray-60'}`}
                onClick={toggleExcluded}
                aria-label={`${isBoneExcluded ? 'Include' : 'Exclude'} bone from mapping`}
                aria-pressed={isBoneExcluded}
              >
                <span
                  className={`block h-2.5 w-2.5 rounded-full bg-white shadow transition-transform ${isBoneExcluded ? 'translate-x-3.5' : 'translate-x-0.5'}`}
                />
              </button>
            </Tooltip>
          </div>
        </div>
      </Section>

      <div ref={transformRowsWidthRef}>
        <PoseTransformEditor
          bone={bone}
          boneIndex={selectedBoneIndex}
          poseVersion={poseVersion}
          compactLayout={compactTransformRows}
          onChange={onBonePoseTransformChange}
          onReset={(boneIndex) =>
            onRecordPoseHistoryAction
              ? onRecordPoseHistoryAction('Reset pose transform', () =>
                  onResetBonePoseTransform?.(boneIndex),
                )
              : onResetBonePoseTransform?.(boneIndex)
          }
          onTranslateValueChangeStart={() =>
            onBeginPoseHistoryTransaction?.('Edit pose translation')
          }
          onTranslateValueChangeEnd={onCommitHistoryTransaction}
          onTranslateDragValueChangeStart={() => {
            onBeginPoseHistoryTransaction?.('Edit pose translation');
            onViewerToolModeChange?.('select');
          }}
          onRotateValueChangeStart={() =>
            onBeginPoseHistoryTransaction?.('Edit pose rotation')
          }
          onRotateValueChangeEnd={onCommitHistoryTransaction}
          onRotateDragValueChangeStart={() => {
            onBeginPoseHistoryTransaction?.('Edit pose rotation');
            onViewerToolModeChange?.('rotate');
          }}
        />
        <RestTransformViewer bone={bone} compactLayout={compactTransformRows} />
      </div>

      {mapping && <MappingInfo bone={bone} mapping={mapping} />}
      {chainName && (
        <SolverEditor
          chainName={chainName}
          solverType={chainMapping?.solverType ?? null}
          solverProperties={chainMapping?.solverProperties ?? null}
          onSolverTypeChange={onChainSolverTypeChange}
          onSolverPropertiesChange={onChainSolverPropertiesChange}
        />
      )}

      {!isBoneExcluded && (
        <Section
          key={`properties-${bone.index}`}
          title="Properties"
          defaultOpen={hasSignificantProperties}
        >
          <div className="space-y-3">
            <RotationLimitsEditor
              bone={bone}
              bump={bump}
              onHistoryAction={onRecordBonePropertiesHistoryAction}
              onValueChangeStart={() =>
                onBeginBonePropertiesHistoryTransaction?.('Edit rotation limits')
              }
              onValueChangeEnd={onCommitHistoryTransaction}
            />
            <PreferredAnglesEditor
              bone={bone}
              bump={bump}
              compactLayout={compactTransformRows}
              onHistoryAction={onRecordBonePropertiesHistoryAction}
              onValueChangeStart={() =>
                onBeginBonePropertiesHistoryTransaction?.(
                  'Edit preferred angle bias',
                )
              }
              onValueChangeEnd={onCommitHistoryTransaction}
              onDragValueChangeStart={() => {
                onBeginBonePropertiesHistoryTransaction?.(
                  'Edit preferred angle bias',
                );
                activatePropertiesToolMode('angle-bias');
              }}
            />
            {mapping && onJointOffsetChange && (
              <div className="border-gray-30/50 dark:border-gray-60/50 border-t">
                <JointOffsetEditor
                  boneRef={bone.internalRef}
                  mapping={mapping}
                  compactLayout={compactTransformRows}
                  onJointOffsetChange={onJointOffsetChange}
                  onHistoryAction={onRecordBonePropertiesHistoryAction}
                  onValueChangeStart={() =>
                    onBeginBonePropertiesHistoryTransaction?.('Edit bone offsets')
                  }
                  onValueChangeEnd={onCommitHistoryTransaction}
                  onDragValueChangeStart={() => {
                    onBeginBonePropertiesHistoryTransaction?.('Edit bone offsets');
                    activatePropertiesToolMode('offset');
                  }}
                />
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
}

function AxisDropdownRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: CharacterAxis;
  onChange: (axis: CharacterAxis) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Text variant="studio/body/medium/xs" className="text-secondary shrink-0">
        {label}
      </Text>
      <Dropdown
        align="end"
        side="bottom"
        inModal
        trigger={
          <button
            type="button"
            className="border-gray-30 dark:border-gray-60 hover:bg-gray-15 dark:hover:bg-gray-75 flex min-w-18 items-center justify-between gap-1 rounded-lg border px-2 py-1 text-left transition-colors"
            aria-label={`${label} axis: ${value}`}
          >
            <Text
              variant="studio/body/medium/sm"
              className="text-primary tabular-nums"
            >
              {value}
            </Text>
            <ChevronDownIcon className="text-gray-45 h-3.5 w-3.5 shrink-0" />
          </button>
        }
      >
        {CHARACTER_AXIS_OPTIONS.map((opt) => (
          <Dropdown.Item
            key={opt}
            onClick={() => onChange(opt)}
            variant={opt === value ? 'blue' : 'default'}
          >
            {opt}
          </Dropdown.Item>
        ))}
      </Dropdown>
    </div>
  );
}

function TransformNodeInspector({
  node,
  hierarchy,
  compactLayout,
  transformRowsWidthRef,
}: {
  node: OutlinerTransformNode;
  hierarchy: OutlinerHierarchy | null;
  compactLayout: boolean;
  transformRowsWidthRef: RefObject<HTMLDivElement | null>;
}) {
  const parentName =
    node.parentId && hierarchy?.nodesById[node.parentId]
      ? hierarchy.nodesById[node.parentId].name
      : 'None';

  let depth = 0;
  let cursor = node.parentId;
  while (cursor && hierarchy?.nodesById[cursor]) {
    depth += 1;
    cursor = hierarchy.nodesById[cursor].parentId;
  }

  const restTranslate = node.offset.translation;
  const restRotate = node.offset.rotation;
  const restScale = node.offset.scale;
  const showRestScale = hasNonIdentityScale(restScale);

  return (
    <div className="translucent-white-scrollbar dark:bg-gray-90/20 flex h-full flex-col overflow-y-auto rounded-xl bg-white/20">
      <Section title="Details" defaultOpen={false}>
        <Row label="Type" value="Transform" />
        <Row label="Parent" value={parentName} />
        <Row label="Children" value={node.childrenIds.length} />
        <Row label="Depth" value={depth} />
      </Section>
      <div ref={transformRowsWidthRef}>
        <Section title="Rest Pose" defaultOpen={false}>
          <div className="space-y-0.5">
            <TransformAxisValuesRow
              label="Translate"
              labelIcon={<MoveIcon className="h-3 w-3" />}
              compact={compactLayout}
              values={restTranslate}
            />
            <TransformAxisValuesRow
              label="Rotate"
              labelIcon={<RotateIcon className="h-3 w-3" />}
              compact={compactLayout}
              values={restRotate}
            />
            {showRestScale && (
              <TransformAxisValuesRow
                label="Scale"
                labelIcon={
                  <Text
                    as="span"
                    variant="studio/body/medium/xs"
                    className="text-primary"
                  >
                    S
                  </Text>
                }
                compact={compactLayout}
                values={restScale}
              />
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
